import { MessageCircle } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

export const procenaKomunikacijeUVezama: QuizConfig = {
  id: "procena-komunikacije-u-vezama",
  illustration: {
    src: "/images/quizzes/procena-komunikacije-u-vezama.webp",
    alt: "Partneri razgovaraju za kuhinjskim stolom, sa telefonima ostavljenim po strani.",
  },
  lastModified: "2026-07-18",
  title: "Da li ti i tvoj partner imate zdravu komunikaciju?",
  shortTitle: "Procena komunikacije u vezi",
  description:
    "Otkrijte koliko je vaša komunikacija sa partnerom zdrava i šta možete poboljšati.",
  longDescription:
    "Proceni kako razgovaraš sa partnerom u stresnim situacijama i koje navike najviše kvare ili grade poverenje. Kroz pitanja o slušanju, svađama i neverbalnim signalima otkrivamo gde se najčešće prekida poverenje i zašto ostajete neshvaćeni nakon razgovora. Na kraju dobijaš personalizovane smernice za zdrave navike komunikacije, predloge kako da otvoreno pričaš o teškim temama i kada je vreme da uključite stručnjaka.",
  icon: MessageCircle,
  duration: "5-7 min",
  category: "Odnosi i komunikacija",
  focusAreasWanted: [
    "communication",
    "relationships",
    "couples_issues",
    "self_development",
  ],
  questions: [
    {
      type: "radio",
      question: "Kako obično razgovarate o važnim temama?",
      options: [
        "Otvoreno i smireno, bez problema",
        "Ponekad se ne slažemo, ali pokušavamo da razumemo jedno drugo",
        "Često se nesporazumemo i završimo svađom",
        "Obično izbegavamo razgovor o teškim temama",
        "Razgovori brzo postanu napeti ili toksični",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako vaš partner reaguje kada mu/joj nešto zasmeta?",
      options: [
        "Smireno izrazi svoje mišljenje",
        "Ponekad se povuče, ali kasnije razgovaramo",
        "Pokušava da mi dokaže da grešim",
        "Često mi prebacuje i kritikuje me",
        "Ignoriše me ili se ljuti bez objašnjenja",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako rešavate nesporazume?",
      options: [
        "Brzo ih rešimo kroz otvoren razgovor",
        "Ponekad treba vremena, ali na kraju pronađemo rešenje",
        "Često ostavimo stvari nerešene i nadamo se da će proći",
        "Svađamo se i teško dolazimo do dogovora",
        "Ignorišemo probleme i pretvaramo se da ne postoje",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Šta se najčešće dešava tokom vaših svađa? (označi sve što važi)",
      options: [
        "Slušamo jedno drugo i pokušavamo da razumemo",
        "Često prekidamo jedno drugo",
        "Vičemo ili podižemo ton",
        "Jedno od nas se povlači i prestaje da razgovara",
        "Posle svađe ostajemo povređeni i ne razgovaramo neko vreme",
      ],
      scores: [0, 2, 2, 3, 3],
    },
    {
      type: "radio",
      question: "Kako se osećaš kada izraziš svoje misli i osećanja?",
      options: [
        "Slobodno i sigurno",
        "Ponekad ne znam kako će reagovati",
        "Često me partner ne razume",
        "Plašim se da kažem šta mislim da ne bih izazvao/la problem",
        "Imam osećaj da moje mišljenje nije važno",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako partner reaguje kada mu/joj kažeš da te nešto povredilo?",
      options: [
        "Razume i pokušava da promeni svoje ponašanje",
        "Ponekad se brani, ali kasnije razmisli o tome",
        "Kaže da preuveličavam",
        "Okrene situaciju i učini da se ja osećam krivo",
        "Ignoriše ili umanjuje moje osećaje",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često iskreno razgovarate o svojim osećanjima?",
      options: [
        "Skoro svakodnevno, bez problema",
        "Povremeno, kada je potrebno",
        "Retko, teško nam je da otvoreno razgovaramo",
        "Skoro nikada, izbegavamo takve razgovore",
        "Ne osećam se prijatno da pričam o svojim osećanjima",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Kako partner reaguje kada se ne slažete oko nečega? (označi sve što važi)",
      options: [
        "Pokušava da pronađe kompromis",
        "Odbija da razgovara o tome",
        "Insistira da je samo on/ona u pravu",
        "Koristi sarkazam ili omalovažava moje mišljenje",
        "Ljuti se i prestaje da razgovara sa mnom",
      ],
      scores: [0, 2, 2, 3, 3],
    },
    {
      type: "radio",
      question: "Kako se osećaš posle razgovora sa partnerom?",
      options: [
        "Razumem stvari bolje i osećam se podržano",
        "Ponekad se osećam neshvaćeno",
        "Često osećam frustraciju ili tugu",
        "Većinom sam iscrpljen/a nakon razgovora",
        "Osećam se kao da ne mogu da iznesem svoje mišljenje",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako vaš partner sluša kada pričate?",
      options: [
        "Aktivno sluša i pokazuje interesovanje",
        "Ponekad deluje odsutno, ali se trudi",
        "Često me prekida ili menja temu",
        "Deluje nezainteresovano ili ne pokazuje emocije",
        "Često skreće razgovor na sebe",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Koji problemi u komunikaciji najčešće postoje u vašoj vezi? (označi sve što važi)",
      options: [
        "Ne slušamo se pažljivo",
        "Brzo planemo i posvađamo se",
        "Jedno od nas se povlači i prestaje da razgovara",
        "Često jedno drugom prebacujemo krivicu",
        "Nedostaje nam emocionalna povezanost u razgovorima",
      ],
      scores: [2, 2, 3, 3, 3],
    },
    {
      type: "radio",
      question: "Kako se nosite sa konfliktima u vezi?",
      options: [
        "Rešavamo ih zajedno i učimo iz njih",
        "Trudimo se da ih rešimo, ali ponekad ostanu nerešeni",
        "Često izbegavamo konflikte da bismo održali mir",
        "Konflikti obično eskaliraju i teško ih rešavamo",
        "Konflikti se ponavljaju i nikad zaista ne budu rešeni",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Koliko često osećate da vas partner zaista razume?",
      options: [
        "Skoro uvek me razume i podržava",
        "Često me razume, ali ponekad imamo nesporazume",
        "Ponekad imam osećaj da me ne razume u potpunosti",
        "Retko osećam da me zaista razume",
        "Imam utisak da me uopšte ne razume",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "checkbox",
      checkboxScoring: "sum",
      question:
        "Šta primećujete u neverbalnoj komunikaciji sa partnerom? (označi sve što važi)",
      options: [
        "Održavamo kontakt očima tokom razgovora",
        "Često se fizički udaljavamo tokom razgovora",
        "Partner često gleda u telefon dok razgovaramo",
        "Govor tela pokazuje nezainteresovanost ili ljutnju",
        "Nedostaje nam fizički kontakt tokom razgovora",
      ],
      scores: [0, 2, 2, 3, 3],
    },
    {
      type: "radio",
      question: "Kako se nosite sa temama koje su vam neprijatne?",
      options: [
        "Otvoreno razgovaramo o svemu, bez tabua",
        "Trudimo se da razgovaramo, ali ponekad je teško",
        "Izbegavamo neke teme jer znamo da će izazvati svađu",
        "Većinu teških tema ostavljamo po strani",
        "Skoro nikad ne razgovaramo o neprijatnim temama",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "radio",
      question: "Kako se osećate kada treba da izrazite svoje potrebe u vezi?",
      options: [
        "Potpuno slobodno i sigurno",
        "Uglavnom prijatno, ali ponekad oklevam",
        "Često se brinem kako će partner reagovati",
        "Teško mi je da izrazim svoje potrebe",
        "Radije prećutim svoje potrebe da bih izbegao/la probleme",
      ],
      scores: [0, 1, 2, 3, 4],
    },
    {
      type: "textarea",
      question: "Koja situacija iz vaše veze pokazuje kako komunicirate?",
    },
  ],
  copy: {
    title: "Nivo vaše komunikacije",
    subtitle: "Razumevanje vaše veze",
    scoreLabel: "Tvoj rezultat:",
    adviceTitle: "Saveti za poboljšanje komunikacije",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
};
